import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './generated/schema';
import { ApiError, type ProblemDetails } from './errors';

export interface ApiClientOptions {
  /** Origin of the .NET API, e.g. http://localhost:5080. No trailing slash. */
  baseUrl: string;

  /** Returns the current access token, or null when the user is anonymous. */
  getAccessToken: () => string | null;

  /**
   * Called on a 401 so the caller can refresh the session. Return the new
   * access token to have the request retried once, or null to give up and let
   * the ApiError propagate.
   */
  onUnauthorized?: () => Promise<string | null>;
}

/**
 * Builds the typed API client.
 *
 * Types come from the generated OpenAPI schema, so a renamed field or a changed
 * response shape is a compile error in the frontend rather than a runtime
 * surprise — which is the entire reason the client is generated instead of
 * hand-written.
 */
export function createApiClient(options: ApiClientOptions) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    credentials: 'include',
    // Resolved on every call rather than captured once.
    //
    // openapi-fetch otherwise binds globalThis.fetch at construction, and this
    // client is built at module scope — so anything that replaces fetch later
    // (a test stub, an instrumentation wrapper) is ignored, and the request goes
    // to the real network while the stub sits unused. That failed silently: the
    // query landed in `error` and the component simply stayed on its skeleton.
    fetch: (request) => globalThis.fetch(request),
  });

  let refreshInFlight: Promise<string | null> | null = null;

  const authMiddleware: Middleware = {
    onRequest({ request }) {
      const token = options.getAccessToken();

      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }

      // Lets a browser request be traced through the API logs by the same id.
      if (!request.headers.has('X-Correlation-Id')) {
        request.headers.set('X-Correlation-Id', crypto.randomUUID());
      }

      return request;
    },

    async onResponse({ request, response }) {
      if (response.status !== 401 || !options.onUnauthorized) {
        return response;
      }

      // Concurrent 401s must trigger ONE refresh, not one per in-flight request.
      refreshInFlight ??= options.onUnauthorized().finally(() => {
        refreshInFlight = null;
      });

      const token = await refreshInFlight;

      if (!token) {
        return response;
      }

      const retry = new Request(request, {
        headers: new Headers(request.headers),
      });
      retry.headers.set('Authorization', `Bearer ${token}`);

      return fetch(retry);
    },
  };

  client.use(authMiddleware);

  return client;
}

/**
 * Normalises an openapi-fetch result into "return data or throw ApiError", which
 * is the contract TanStack Query expects.
 */
export async function unwrap<TData>(
  promise: Promise<{ data?: TData; error?: unknown; response: Response }>,
): Promise<TData> {
  const { data, error, response } = await promise;

  if (error !== undefined || !response.ok) {
    throw new ApiError(response.status, error as ProblemDetails | undefined);
  }

  return data as TData;
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * Uploads a file as multipart/form-data.
 *
 * Separate from the typed client because openapi-fetch serialises bodies as
 * JSON, and multipart needs the browser to build the body itself so the boundary
 * is generated. Everything else — bearer token, correlation id, one refresh and
 * one retry on 401 — matches the typed client exactly.
 */
export function createFileUploader(options: ApiClientOptions) {
  let refreshInFlight: Promise<string | null> | null = null;

  return async function uploadFile<TResponse>(
    path: string,
    file: File,
    fieldName = 'file',
  ): Promise<TResponse> {
    const send = (token: string | null): Promise<Response> => {
      const form = new FormData();
      form.append(fieldName, file);

      const headers = new Headers();
      headers.set('X-Correlation-Id', crypto.randomUUID());

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      // Content-Type is deliberately NOT set: the browser adds it along with the
      // multipart boundary, and setting it by hand makes the server unable to
      // parse the body.
      return fetch(`${options.baseUrl}${path}`, {
        method: 'POST',
        body: form,
        headers,
        credentials: 'include',
      });
    };

    let response = await send(options.getAccessToken());

    if (response.status === 401 && options.onUnauthorized) {
      refreshInFlight ??= options.onUnauthorized().finally(() => {
        refreshInFlight = null;
      });

      const token = await refreshInFlight;

      if (token) {
        response = await send(token);
      }
    }

    if (!response.ok) {
      let problem: ProblemDetails | undefined;

      try {
        problem = (await response.json()) as ProblemDetails;
      } catch {
        // A non-JSON error body (a proxy's HTML 502 page, say) leaves the
        // ApiError with just its status, which is still better than throwing
        // a parse error over the real failure.
      }

      throw new ApiError(response.status, problem);
    }

    return (await response.json()) as TResponse;
  };
}

export type FileUploader = ReturnType<typeof createFileUploader>;
